import { FullName, LocationInfo, PhoneNumber } from 'src/api/common';
import {
  AutocompletedField,
  AutocompletedFieldId,
  AutofilledProfilePatchDto,
  Experience,
  ExperienceDto,
  LanguageLevelDto,
  PersonJobRoleReferenceDto,
  PersonJobRoleSkillReferenceDto
} from 'src/api/person-profile/types';
import {
  MAX_ROLES,
  MAX_SKILLS_PER_ROLE,
  OnBoardingPersonProfile,
  WorkRoleSkillMethodologyExperience,
  getExperienceCode,
  experienceRowKey
} from 'src/pages/onboarding/create-profile/person/types';
import { isNullOrWhiteSpaces } from 'src/shared/utils/strings';

/**
 * The items of `incoming` the selection does not hold yet, appended to it within `max` — or null
 * when nothing fits, so state updaters can return the previous array identity and break the
 * selection → onChange → data → merge render cycle. Math.max because a selection can already exceed
 * the cap (legacy profiles), and Array.slice with a negative end counts from the end of the array
 * instead of returning nothing.
 */
export function appendIncoming<T>(curr: T[], incoming: T[], max: number, keyOf: (item: T) => string | undefined): T[] | null {
  const freeSlots = Math.max(0, max - curr.length);
  if (freeSlots === 0) return null;

  const known = new Set(curr.map(keyOf));
  const newOnes = incoming.filter((x) => !known.has(keyOf(x))).slice(0, freeSlots);

  return newOnes.length === 0 ? null : [...curr, ...newOnes];
}

export function extractAutocompletedFields(dto: Partial<AutofilledProfilePatchDto>): AutocompletedField[] {
  const fields: AutocompletedField[] = [];

  if (dto.fullName) {
    fields.push({
      id: 'fullName',
      label: 'Nombre y apellidos',
      value: dto.fullName
    });
  }

  if (dto.phoneNumber) {
    fields.push({
      id: 'phoneNumber',
      label: 'Teléfono',
      value: dto.phoneNumber
    });
  }

  if (dto.workLocation) {
    fields.push({
      id: 'workLocation',
      label: 'Ubicación',
      value: dto.workLocation
    });
  }

  if (dto.serviceDefinition || dto.serviceDefinitionPhrase) {
    fields.push({
      id: 'serviceDefinition',
      label: 'Descripción del servicio',
      value: { serviceDefinition: dto.serviceDefinition, serviceDefinitionPhrase: dto.serviceDefinitionPhrase }
    });
  }

  if (dto.languages && dto.languages.length > 0) {
    fields.push({
      id: 'language',
      label: 'Idiomas',
      value: dto.languages
    });
  }

  if (dto.certifications && dto.certifications.length > 0) {
    fields.push({
      id: 'certifications',
      label: 'Cursos y certificaciones',
      value: dto.certifications
    });
  }

  if (dto.education && dto.education.length > 0) {
    fields.push({
      id: 'education',
      label: 'Formación académica',
      value: dto.education
    });
  }

  if (dto.experience && dto.experience.length > 0) {
    fields.push({
      id: 'experience',
      label: 'Experiencia laboral',
      value: dto.experience
    });
  }

  if (dto.roles && dto.roles.length > 0) {
    fields.push({
      id: 'roles',
      label: 'Roles',
      value: dto.roles
    });
  }

  if (dto.skills && dto.skills.length > 0) {
    fields.push({
      id: 'skills',
      label: 'Habilidades',
      value: dto.skills
    });
  }

  return fields;
}

type FieldMapper = (value: any, existing: OnBoardingPersonProfile, result: Partial<OnBoardingPersonProfile>) => void;

const FIELD_MAPPERS: Record<string, FieldMapper> = {
  fullName: (value, existing, result) => {
    if (!existing.fullName) {
      result.fullName = value;
      return;
    }
    const { firstName, lastName } = existing.fullName;
    if (!firstName && value.firstName) {
      result.fullName = result.fullName ?? { ...existing.fullName };
      result.fullName.firstName = value.firstName;
    }
    if (!lastName && value.lastName) {
      result.fullName = result.fullName ?? { ...existing.fullName };
      result.fullName.lastName = value.lastName;
    }
  },

  language: (value, existing, result) => {
    const existingIds = new Set(existing.languages?.map((l) => l.languageId) ?? []);
    const newLanguages = value.filter((l: any) => !existingIds.has(l.languageId));
    if (newLanguages.length > 0) {
      result.languages = [
        ...(result.languages ?? []),
        ...newLanguages.map((l: any) => ({
          languageId: l.languageId,
          language: l.language,
          level: l.level,
          order: l.order
        }))
      ];
    }
  },

  phoneNumber: (value, existing, result) => {
    const existingPhone = existing.contactInfo?.phoneNumber;
    if (!existingPhone && value.number) {
      result.contactInfo = {
        ...existing.contactInfo,
        phoneNumber: value.number,
        phoneNumberCountryId: value.countryId,
        phoneNumberCountryName: existing.contactInfo?.phoneNumberCountryName ?? ''
      };
    }
  },

  workLocation: (value, existing, result) => {
    const existingLocation = existing.professionalInfo?.location;
    if (!existingLocation && value) {
      result.professionalInfo = {
        ...existing.professionalInfo,
        location: value
      };
    }
  },

  serviceDefinition: (value, existing, result) => {
    const resultService: Partial<OnBoardingPersonProfile['serviceInfo']> = {};

    if (!existing.serviceInfo?.serviceDefinition && value.serviceDefinition) {
      resultService.serviceDefinition = value.serviceDefinition;
    }

    if (!existing.serviceInfo?.serviceDefinitionPhrase && value.serviceDefinitionPhrase) {
      resultService.serviceDefinitionPhrase = value.serviceDefinitionPhrase;
    }

    if (Object.keys(resultService).length > 0) {
      result.serviceInfo = {
        ...existing.serviceInfo,
        ...resultService
      };
    }
  },

  roles: (value, existing, result) => {
    const currentRoles = existing.roles ?? [];

    // The autofill counted the free slots against the roles the profile had when it ran; the person
    // may have picked more since, without having submitted the step yet. Only their own selection is
    // sure to be there, so the imported roles take what is left of the 3 — dropping the rest, which
    // would otherwise reach the skills and experience steps as roles the profile does not hold.
    const freeSlots = MAX_ROLES - currentRoles.length;
    if (freeSlots <= 0) return;

    const existingIds = new Set(currentRoles.map((r) => r.id));
    const newRoles = (value as PersonJobRoleReferenceDto[]).filter((r) => !existingIds.has(r.roleId)).slice(0, freeSlots);
    if (newRoles.length === 0) return;

    result.roles = [
      ...currentRoles,
      ...newRoles.map((r) => ({
        id: r.roleId,
        label: r.roleName,
        order: r.order
      }))
    ];

    // The autofill already derived the years of experience for each role. Without carrying them
    // over, the experience step would open blank and overwrite them on submit.
    appendExperiences(
      result,
      existing,
      newRoles.map((r) => experienceRow(r.experience, { roleId: r.roleId, role: r.roleName }))
    );
  },

  skills: (value, existing, result) => {
    // The roles mapper runs first, so a skill hanging off a just-imported role finds its role here
    const roles = result.roles ?? existing.roles ?? [];
    const currentSkills = existing.skills ?? [];
    const existingIds = new Set(currentSkills.map((s) => s.id));

    // Each role's skills are submitted as one batch, so the limit applies per role — and a skill
    // whose role did not make it into the selection has nothing to hang off at all.
    const freeSlotsByRole = new Map(roles.map((r) => [r.id, MAX_SKILLS_PER_ROLE - currentSkills.filter((s) => s.roleId === r.id).length]));

    const newSkills: PersonJobRoleSkillReferenceDto[] = [];

    for (const skill of value as PersonJobRoleSkillReferenceDto[]) {
      const freeSlots = freeSlotsByRole.get(skill.roleId);
      if (existingIds.has(skill.skillId) || !freeSlots || freeSlots <= 0) continue;

      freeSlotsByRole.set(skill.roleId, freeSlots - 1);
      newSkills.push(skill);
    }

    if (newSkills.length === 0) return;

    result.skills = [
      ...currentSkills,
      ...newSkills.map((s) => ({
        id: s.skillId,
        roleId: s.roleId,
        label: s.skillName,
        order: s.order
      }))
    ];

    appendExperiences(
      result,
      existing,
      newSkills.map((s) =>
        experienceRow(s.experience, {
          roleId: s.roleId,
          role: roles.find((r) => r.id === s.roleId)?.label as string,
          skill: s.skillName,
          skillId: s.skillId
        })
      )
    );
  }
};

type ExperienceRowKeys = Pick<WorkRoleSkillMethodologyExperience, 'roleId' | 'role'> &
  Partial<Pick<WorkRoleSkillMethodologyExperience, 'skill' | 'skillId'>>;

/** One row of the experience step, with the years the autofill derived — or blank when it derived none. */
function experienceRow(experience: ExperienceDto | null | undefined, keys: ExperienceRowKeys): WorkRoleSkillMethodologyExperience {
  return {
    ...keys,
    skill: keys.skill as string,
    code: getExperienceCode(experience as Experience) as string,
    ge: experience?.yearsGreatEqThan as number,
    le: experience?.yearsLessEqThan,
    favorite: false
  };
}

/** Adds rows the profile does not have yet, so neither mapper drops what the other one contributed. */
function appendExperiences(result: Partial<OnBoardingPersonProfile>, existing: OnBoardingPersonProfile, rows: WorkRoleSkillMethodologyExperience[]) {
  const current = result.experiences ?? existing.experiences ?? [];
  const known = new Set(current.map(experienceRowKey));

  result.experiences = [...current, ...rows.filter((row) => !known.has(experienceRowKey(row)))];
}

export function mapAutocompletedFieldsToProfile(
  fields: AutocompletedField[],
  existingProfile: OnBoardingPersonProfile
): Partial<OnBoardingPersonProfile> {
  const result: Partial<OnBoardingPersonProfile> = {};

  for (const { id, value } of fields) {
    const fn = FIELD_MAPPERS[id];
    if (fn) {
      fn(value, existingProfile, result);
    }
  }

  return result;
}

const COMPARISON_HANDLERS: {
  [K in AutocompletedFieldId]?: (value: any, profile: OnBoardingPersonProfile) => boolean | null;
} = {
  fullName: (value, profile) => {
    const fullName = value as FullName;
    if (isNullOrWhiteSpaces(profile.fullName?.firstName) || isNullOrWhiteSpaces(profile.fullName.lastName)) return null;
    return fullName.firstName === profile.fullName.firstName && fullName.lastName === profile.fullName.lastName;
  },

  language: (value, profile) => {
    if (!profile.languages || profile.languages.length === 0) return null;
    const newLanguages = value as LanguageLevelDto[];
    const existingSet = new Set(profile.languages.map((l) => `${l.languageId}-${l.level}`));
    return newLanguages.some((l) => existingSet.has(`${l.languageId}-${l.level}`));
  },

  phoneNumber: (value, profile) => {
    const newContact = value as PhoneNumber;
    const existing = profile.contactInfo;
    if (existing == null || (isNullOrWhiteSpaces(existing.phoneNumber) && existing.phoneNumberCountryId == null)) return null;
    return newContact.number === existing.phoneNumber && newContact.countryId === existing.phoneNumberCountryId;
  },

  workLocation: (value, profile) => {
    const newLocation = value as LocationInfo;
    const existing = profile.professionalInfo.location?.label;
    if (isNullOrWhiteSpaces(existing)) return null;
    return newLocation.label === existing;
  },

  serviceDefinition: (value, profile) => {
    const newService = value as { serviceDefinition: string; serviceDefinitionPhrase: string };
    const existing = profile.serviceInfo;
    if (existing == null || (isNullOrWhiteSpaces(existing.serviceDefinition) && isNullOrWhiteSpaces(existing.serviceDefinitionPhrase))) return null;
    return newService.serviceDefinition === existing.serviceDefinition || newService.serviceDefinitionPhrase === existing.serviceDefinitionPhrase;
  },

  roles: (value, profile) => {
    if (!profile.roles || profile.roles.length === 0) return null;
    const newRoles = value as PersonJobRoleReferenceDto[];
    const existingSet = new Set(profile.roles.map((r) => r.id));
    return newRoles.some((r) => existingSet.has(r.roleId));
  },

  skills: (value, profile) => {
    if (!profile.skills || profile.skills.length === 0) return null;
    const newSkills = value as PersonJobRoleSkillReferenceDto[];
    const existingSet = new Set(profile.skills.map((s) => s.id));
    return newSkills.some((s) => existingSet.has(s.skillId));
  }
};

export function areAllFieldsNotAutocompleted(
  fieldIds: AutocompletedFieldId[],
  fields: AutocompletedField[],
  existingProfile: OnBoardingPersonProfile
): boolean {
  if (fieldIds.length === 0 || fields.length === 0) return false;

  const fieldMap = new Map<string, AutocompletedField>();
  for (const field of fields) {
    fieldMap.set(field.id, field);
  }

  for (const id of fieldIds) {
    const compareFn = COMPARISON_HANDLERS[id];
    const field = fieldMap.get(id);
    if (field?.value && compareFn?.(field.value, existingProfile) !== false) {
      return false;
    }
  }

  return true;
}
